import { useState } from 'react';
import { Chip, InputLabel, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import dayjs from '@utils/dayjs.utils';
import _ from 'lodash';

interface MultiDatePickerComponentProps {
  color?: 'primary' | 'secondary';
  disableFuture?: boolean;
  disablePast?: boolean;
  error?: string;
  format?: string;
  handleChange: (dates: string[]) => void;
  label?: string;
  labelWidth?: number;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  values?: any[];
}

const MultiDatePickerComponent = (props: MultiDatePickerComponentProps) => {
  const {
    color = 'primary',
    disableFuture,
    disablePast,
    error,
    format,
    handleChange,
    label,
    labelWidth,
    placeholder,
    readOnly,
    required,
    values = [],
  } = props;

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>();

  const handleDatePickerSaveValue = (date: any) => {
    if (date && !_.includes('DD/MM/YYYY', date)) {
      handleChange([...values, date]);
      setInputValue('');
      setOpen(false);
    }
  };

  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      {!!label && (
        <InputLabel error={!!error} sx={{ width: labelWidth ? `${labelWidth}px` : undefined }}>
          {label + (required ? '*' : '')}
        </InputLabel>
      )}
      <Stack flex={1} >
        <DatePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onAccept={handleDatePickerSaveValue}
          readOnly={readOnly}
          disablePast={disablePast}
          disableFuture={disableFuture}
          shouldDisableDate={(date) => values.some((d) => dayjs(d).isSame(date, 'day'))}
          value={inputValue?.length ? dayjs(inputValue) : null}
          format={'DD/MM/YYYY'}
          sx={{
            '& .MuiFilledInput': {
              padding: '0px 0px !important',
            },

          }}
          onChange={(date) => {
            setOpen(false);
            date && date.isValid() && setInputValue((date as any).toISOString());
          }}
          slotProps={{
            popper: {
              style: { zIndex: 1301 },
            },
            inputAdornment: {
              position: 'start',
              sx: { ml: '8px !important' }
            },
            openPickerIcon: {
              tabIndex: -1
            },
            openPickerButton: {
              tabIndex: -1
            },
            textField: {
              sx: {
                flex: 1,
                '& .MuiInputBase-root': {
                  borderRadius: values.length > 0 ? '6px 6px 0px 0px !important' : undefined,
                }
              },
              onKeyDown: (e) => {
                if (e.defaultPrevented) { return; }

                e.stopPropagation();

                if (e.key === 'Enter' && inputValue && !_.includes('DD/MM/YYYY', inputValue)) {
                  handleChange([...values, inputValue]);
                  setInputValue('');
                  setOpen(false);
                }
              },
              color: color,
              focused: readOnly ? false : undefined,
              inputProps: {
                placeholder: placeholder,
              },
              error: !!error,
              helperText: error,
            }
          }}
        />
        {values.length > 0 && (
          <Stack
            direction='row'
            gap={1}
            bgcolor='#F4F6F8'
            p={2}
            borderRadius={'0px 0px 6px 6px'}
            flexWrap={'wrap'}>
            {values.map((date: string, index: number) => (
              <Chip
                key={index}
                variant="filled"
                color={color}
                size="small"
                sx={{ width: 'fit-content' }}
                label={dayjs(date).format(format || 'DD/MM/YYYY')}
                onDelete={!readOnly
                  ? () => handleChange(values.filter((_, valueIndex) => valueIndex !== index))
                  : undefined
                } />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack >
  );
};

export default MultiDatePickerComponent;
